import React, { FC, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Button as MUIButton,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  Link,
  Tooltip,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { formatInBackofficeLondon } from "@/utils/dateUtils";
import { useTranslation } from "react-i18next";
import { usePageStore } from "@/hooks/usePageStore";
import { useGetSettings } from "@/hooks/useSetting";
import { useGeofenceById } from "@/hooks/useGeofences";
import Breadcrumbs from "@/components/Breadcrumbs";
import Spinner from "@/components/Spinner";
import GeofenceMap, {
  GeofenceMapPoint,
} from "@/components/GeofenceMap";
import {
  GeoFenceAction,
  GeoFenceAddressType,
  ValidationRequestSettings,
} from "@/domain/geofence.interface";

const geofenceSettingsFields: (keyof ValidationRequestSettings)[] = [
  "metersRadiusGeofenceRequestValidation",
  "metersGeofenceAccuracyThreshold",
  "minutesPingGeofenceValidationRequest",
  "minutesPingGeofenceAlive",
  "automaticGeofenceTrigger",
  "requestGeofenceValidationsPerDay",
  "minimumMinsGeofenceBetweenRequestValidations",
  "showGeofenceChip",
  "distanceFilterGeofenceRequestValidation",
  "minutesTimeToCompleteValidation",
  "validationRequestMaxAttempts",
];

const FieldRow: FC<{
  label: string;
  value: React.ReactNode;
  tooltip?: string;
}> = ({ label, value, tooltip }) => (
  <Box sx={{ display: "flex", py: 0.75, alignItems: "flex-start" }}>
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.5,
        minWidth: 240,
      }}
    >
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ fontWeight: 600 }}
      >
        {label}
      </Typography>
      {tooltip && (
        <Tooltip title={tooltip} arrow placement="top">
          <InfoOutlinedIcon
            sx={{ fontSize: 14, color: "text.secondary", cursor: "help" }}
          />
        </Tooltip>
      )}
    </Box>
    <Typography variant="body2" sx={{ wordBreak: "break-word" }} component="div">
      {value ?? "-"}
    </Typography>
  </Box>
);

const formatNumber = (n?: number, digits = 1): string => {
  if (n === undefined || n === null || Number.isNaN(n)) return "-";
  return Number(n).toFixed(digits);
};

const renderMetadata = (data?: Record<string, unknown>): React.ReactNode => {
  if (!data || Object.keys(data).length === 0) return "-";
  return (
    <Box
      component="pre"
      sx={{
        m: 0,
        p: 1,
        backgroundColor: "rgba(0,0,0,0.04)",
        borderRadius: 1,
        fontSize: 12,
        whiteSpace: "pre-wrap",
        wordBreak: "break-word",
      }}
    >
      {JSON.stringify(data, null, 2)}
    </Box>
  );
};

const GeofenceDetailPage: FC = () => {
  const { geofenceId } = useParams<{ geofenceId: string }>();
  const { setPageTitle } = usePageStore();
  const { t } = useTranslation("geofence");
  const tt = t as (key: string) => string;
  const { data: settings } = useGetSettings();
  const navigate = useNavigate();

  const { data: geofence, isLoading } = useGeofenceById(geofenceId || "");

  const dateFormat =
    (settings?.generalDateFormat as string) || "DD MMM YYYY, hh:mm A";

  useEffect(() => {
    setPageTitle(t("detail.title"));
  }, []);

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}>
        <Spinner />
      </Box>
    );
  }

  if (!geofence) {
    return (
      <Box>
        <Breadcrumbs
          items={[
            { label: t("pageTitle"), href: "/geofences", isLink: true },
            { label: t("detail.title"), isLink: false },
          ]}
        />
        <Typography sx={{ mt: 4 }}>Geofence not found.</Typography>
      </Box>
    );
  }

  const validationSettings =
    geofence.program?.configuration?.validationRequestSettings;

  const address = geofence.participant?.address;
  const isSecondary = geofence.addressType === GeoFenceAddressType.SECONDARY;

  const primaryAddressLine = address
    ? [address.addressLine1, address.addressLine2].filter(Boolean).join(", ")
    : "";
  const secondaryAddressLine = address
    ? [address.secondaryAddressLine1, address.secondaryAddressLine2]
        .filter(Boolean)
        .join(", ")
    : "";

  const fmtDate = (d?: string) =>
    d ? formatInBackofficeLondon(d, dateFormat) : "-";

  const centerCoords =
    address &&
    (isSecondary
      ? address.secondaryLatitude != null && address.secondaryLongitude != null
        ? { lat: address.secondaryLatitude, lon: address.secondaryLongitude }
        : null
      : address.latitude != null && address.longitude != null
        ? { lat: address.latitude, lon: address.longitude }
        : null);

  type VerdictSeverity = "success" | "warning" | "error" | "info" | "default";
  type Verdict = {
    severity: VerdictSeverity;
    headline: string;
    detail: string;
  };

  const buildVerdict = (): Verdict => {
    const isEnter = geofence.action === GeoFenceAction.ENTER;
    const d = geofence.distanceToCenterMeters;
    const r = geofence.geofenceRadiusMeters;
    const distanceLabel =
      d != null && r != null
        ? `${tt("detail.verdict.distancePrefix")} ${formatNumber(d, 1)} m ${tt("detail.verdict.distanceMiddle")} ${r} m.`
        : tt("detail.verdict.noDistance");

    if (isEnter) {
      return {
        severity: "info",
        headline: tt("detail.verdict.enter"),
        detail: distanceLabel,
      };
    }

    if (geofence.isPhantom === true) {
      return {
        severity: "error",
        headline: tt("detail.verdict.phantom"),
        detail: distanceLabel,
      };
    }

    if (geofence.isPhantom === false) {
      const sent = !!geofence.testResult?._id;
      return {
        severity: sent ? "success" : "warning",
        headline: sent
          ? tt("detail.verdict.realSent")
          : tt("detail.verdict.realNoVr"),
        detail: distanceLabel,
      };
    }

    // COYN-1347: isPhantom is undefined → the gate did not run on this
    // record (it predates the deployment). Fall back to the server-computed
    // isInsideRadius so the user gets a verdict instead of a dead-end "Not
    // classified" when the distance and radius are sitting right next to it.
    // The headline carries an "(inferred)" suffix to keep the semantic
    // distinction visible: a real flag set by the gate is not the same as a
    // verdict derived post-hoc from coordinates.
    if (geofence.isInsideRadius === true) {
      return {
        severity: "warning",
        headline: tt("detail.verdict.inferredPhantom"),
        detail: distanceLabel,
      };
    }

    if (geofence.isInsideRadius === false) {
      const sent = !!geofence.testResult?._id;
      return {
        severity: sent ? "success" : "info",
        headline: sent
          ? tt("detail.verdict.inferredRealSent")
          : tt("detail.verdict.inferredReal"),
        detail: distanceLabel,
      };
    }

    return {
      severity: "default",
      headline: tt("detail.verdict.unclassified"),
      detail: tt("detail.verdict.unclassifiedDetail"),
    };
  };

  const verdict = buildVerdict();

  const verdictColors: Record<
    VerdictSeverity,
    { bg: string; border: string; text: string }
  > = {
    success: {
      bg: "rgba(46,125,50,0.08)",
      border: "success.main",
      text: "success.main",
    },
    warning: {
      bg: "rgba(237,108,2,0.08)",
      border: "warning.main",
      text: "warning.main",
    },
    error: {
      bg: "rgba(244,67,54,0.10)",
      border: "error.main",
      text: "error.main",
    },
    info: {
      bg: "rgba(2,136,209,0.08)",
      border: "info.main",
      text: "info.main",
    },
    default: {
      bg: "rgba(0,0,0,0.04)",
      border: "divider",
      text: "text.primary",
    },
  };
  const vc = verdictColors[verdict.severity];

  // Build the three map points (primary address, secondary address, SDK
  // event coords). Each one is independently optional — we always have the
  // event coords, but participant addresses may lack lat/lng. The radius
  // circle is anchored on whichever address matches the event's addressType.
  const primaryPoint: GeofenceMapPoint | undefined =
    address?.latitude != null && address?.longitude != null
      ? {
          lat: address.latitude,
          lng: address.longitude,
          label: primaryAddressLine || "—",
        }
      : undefined;
  const secondaryPoint: GeofenceMapPoint | undefined =
    address?.secondaryLatitude != null && address?.secondaryLongitude != null
      ? {
          lat: address.secondaryLatitude,
          lng: address.secondaryLongitude,
          label: secondaryAddressLine || "—",
        }
      : undefined;
  const eventPoint: GeofenceMapPoint | undefined =
    typeof geofence.latitude === "number" &&
    typeof geofence.longitude === "number"
      ? {
          lat: geofence.latitude,
          lng: geofence.longitude,
          label: `${fmtDate(geofence.timestamp)} • ${geofence.action}`,
        }
      : undefined;
  const activeCenterPoint = isSecondary ? secondaryPoint : primaryPoint;

  return (
    <Box>
      <Breadcrumbs
        items={[
          { label: t("pageTitle"), href: "/geofences", isLink: true },
          { label: t("detail.title"), isLink: false },
        ]}
      />

      <MUIButton
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate(-1)}
        sx={{ mt: 1, mb: 1 }}
      >
        {t("detail.back")}
      </MUIButton>

      <Grid container spacing={3} sx={{ mt: 0 }}>
        {/* Verdict banner — at-a-glance summary */}
        <Grid item xs={12}>
          <Box
            sx={{
              p: 2,
              borderLeft: "4px solid",
              borderColor: vc.border,
              backgroundColor: vc.bg,
              borderRadius: 1,
            }}
          >
            <Typography
              variant="h6"
              sx={{ fontWeight: 700, color: vc.text, mb: 0.5 }}
            >
              {verdict.headline}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {verdict.detail}
            </Typography>
          </Box>
        </Grid>

        {/* Map — primary, secondary, SDK event coords + radius circle */}
        {eventPoint && (
          <Grid item xs={12}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" sx={{ mb: 1, fontWeight: 700 }}>
                  {t("detail.mapTitle")}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  {t("detail.mapHint")}
                </Typography>
                <GeofenceMap
                  primary={primaryPoint}
                  secondary={secondaryPoint}
                  event={eventPoint}
                  activeCenter={activeCenterPoint}
                  radiusMeters={geofence.geofenceRadiusMeters}
                  // COYN-1347: feed the phantom-EXIT gate buffer + the
                  // trajectory / heatmap overlays. All three props are
                  // optional on the map component, so the existing layout
                  // degrades gracefully when any of them is absent.
                  accuracyThresholdMeters={
                    validationSettings?.metersGeofenceAccuracyThreshold
                  }
                  participantId={geofence.participant?._id}
                  currentEventId={geofence._id}
                  labels={{
                    primary: t("detail.map.primary"),
                    secondary: t("detail.map.secondary"),
                    event: t("detail.map.event"),
                    radius: t("detail.map.radius"),
                    openInGoogleMaps: t("detail.map.openInGoogleMaps"),
                    bufferCircle: t("detail.map.bufferCircle"),
                    trajectory: t("detail.map.trajectory"),
                    trajectoryHint: t("detail.map.trajectoryHint"),
                    heatmap: t("detail.map.heatmap"),
                    tooltipPhantom: t("detail.map.eventTooltip.phantom"),
                    tooltipInferredPhantom: t(
                      "detail.map.eventTooltip.inferredPhantom",
                    ),
                    tooltipReal: t("detail.map.eventTooltip.real"),
                    tooltipInferredReal: t(
                      "detail.map.eventTooltip.inferredReal",
                    ),
                    dateFormat,
                  }}
                />
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Troubleshooting (phantom + distance + decision logic) */}
        <Grid item xs={12}>
          <Card
            variant="outlined"
            sx={{
              borderColor: geofence.isPhantom ? "error.main" : undefined,
              borderWidth: geofence.isPhantom ? 2 : 1,
            }}
          >
            <CardContent>
              <Typography variant="h6" sx={{ mb: 1, fontWeight: 700 }}>
                {t("detail.troubleshooting")}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t("detail.troubleshootingHint")}
              </Typography>
              <Grid container spacing={0}>
                <Grid item xs={12} md={6}>
                  <FieldRow
                    label={t("detail.fields.isPhantom")}
                    tooltip={t("detail.tooltips.isPhantom")}
                    value={
                      geofence.isPhantom === true ? (
                        <Chip
                          label={t("phantom.yes")}
                          size="small"
                          color="error"
                          variant="filled"
                        />
                      ) : geofence.isPhantom === false ? (
                        <Chip
                          label={t("phantom.no")}
                          size="small"
                          color="success"
                          variant="outlined"
                        />
                      ) : (
                        <Chip
                          label={t("phantom.unclassified")}
                          size="small"
                          color="default"
                          variant="outlined"
                        />
                      )
                    }
                  />
                  <FieldRow
                    label={t("detail.fields.distanceToCenter")}
                    tooltip={t("detail.tooltips.distanceToCenter")}
                    value={
                      geofence.distanceToCenterMeters != null
                        ? `${formatNumber(geofence.distanceToCenterMeters, 1)} m`
                        : "-"
                    }
                  />
                  <FieldRow
                    label={t("detail.fields.geofenceRadius")}
                    tooltip={t("detail.tooltips.geofenceRadius")}
                    value={
                      geofence.geofenceRadiusMeters != null
                        ? `${geofence.geofenceRadiusMeters} m`
                        : "-"
                    }
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FieldRow
                    label={t("detail.fields.isInsideRadius")}
                    tooltip={t("detail.tooltips.isInsideRadius")}
                    value={
                      geofence.isInsideRadius === true ? (
                        <Chip
                          label={t("inside.yes")}
                          size="small"
                          color="info"
                          variant="filled"
                        />
                      ) : geofence.isInsideRadius === false ? (
                        <Chip
                          label={t("inside.no")}
                          size="small"
                          color="default"
                          variant="outlined"
                        />
                      ) : (
                        "-"
                      )
                    }
                  />
                  <FieldRow
                    label={t("detail.fields.locationAccuracy")}
                    tooltip={t("detail.tooltips.locationAccuracy")}
                    value={
                      geofence.locationAccuracy != null
                        ? `${geofence.locationAccuracy} m`
                        : "-"
                    }
                  />
                  <FieldRow
                    label={t("detail.fields.addressType")}
                    tooltip={t("detail.tooltips.addressType")}
                    value={
                      geofence.addressType
                        ? t(`addressType.${geofence.addressType}`)
                        : "-"
                    }
                  />
                </Grid>
              </Grid>

              {/* Decision logic + coordinate comparison */}
              {geofence.distanceToCenterMeters != null &&
                geofence.geofenceRadiusMeters != null &&
                centerCoords && (
                  <>
                    <Divider sx={{ my: 2 }} />
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: 700, mb: 1 }}
                    >
                      {t("detail.decisionLogic")}
                    </Typography>
                    <Box
                      sx={{
                        p: 1.5,
                        backgroundColor: geofence.isPhantom
                          ? "rgba(244,67,54,0.08)"
                          : "rgba(0,0,0,0.04)",
                        borderRadius: 1,
                        fontFamily: "monospace",
                        fontSize: 13,
                        whiteSpace: "pre-wrap",
                        mb: 2,
                      }}
                    >
                      {(() => {
                        const d = geofence.distanceToCenterMeters!;
                        const r = geofence.geofenceRadiusMeters!;
                        const inside = geofence.isInsideRadius === true;
                        const margin = Math.abs(r - d);
                        const op = inside ? "<" : "≥";
                        const verdict = inside
                          ? t("detail.decisionInsideVerdict")
                          : t("detail.decisionOutsideVerdict");
                        const phantomTail = geofence.isPhantom
                          ? `\n→ ${t("detail.decisionPhantom")}`
                          : "";
                        return `distance ${formatNumber(d, 1)} m  ${op}  radius ${r} m\n→ ${verdict} (${formatNumber(margin, 1)} m ${inside ? t("detail.decisionMarginInside") : t("detail.decisionMarginOutside")})${phantomTail}`;
                      })()}
                    </Box>

                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: 700, mb: 1 }}
                    >
                      {t("detail.comparison")}
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <Box
                          sx={{
                            p: 1.5,
                            border: "1px solid",
                            borderColor: "divider",
                            borderRadius: 1,
                          }}
                        >
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontWeight: 700 }}
                          >
                            {t("detail.eventCoords")}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ fontFamily: "monospace", mt: 0.5 }}
                          >
                            lat: {geofence.latitude}
                            <br />
                            lon: {geofence.longitude}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Box
                          sx={{
                            p: 1.5,
                            border: "1px solid",
                            borderColor: "divider",
                            borderRadius: 1,
                          }}
                        >
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontWeight: 700 }}
                          >
                            {t("detail.centerCoords")} (
                            {geofence.addressType
                              ? t(`addressType.${geofence.addressType}`)
                              : "-"}
                            )
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ fontFamily: "monospace", mt: 0.5 }}
                          >
                            lat: {centerCoords.lat}
                            <br />
                            lon: {centerCoords.lon}
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>

                    <Box sx={{ mt: 1.5 }}>
                      <FieldRow
                        label={t("detail.fields.deltaLat")}
                        value={
                          centerCoords.lat != null && geofence.latitude != null
                            ? formatNumber(
                                geofence.latitude - centerCoords.lat,
                                6,
                              )
                            : "-"
                        }
                      />
                      <FieldRow
                        label={t("detail.fields.deltaLon")}
                        value={
                          centerCoords.lon != null && geofence.longitude != null
                            ? formatNumber(
                                geofence.longitude - centerCoords.lon,
                                6,
                              )
                            : "-"
                        }
                      />
                    </Box>
                  </>
                )}
            </CardContent>
          </Card>
        </Grid>

        {/* Event Information (SDK) */}
        <Grid item xs={12} md={6}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6" sx={{ mb: 0.5, fontWeight: 700 }}>
                {t("detail.eventInfoSdk")}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 2 }}
              >
                {t("detail.eventInfoSdkHint")}
              </Typography>
              <FieldRow
                label={t("detail.fields.action")}
                value={
                  <Chip
                    label={t(`action.${geofence.action}`)}
                    size="small"
                    color={
                      geofence.action === GeoFenceAction.ENTER
                        ? "success"
                        : "warning"
                    }
                    variant="outlined"
                  />
                }
              />
              <FieldRow
                label={t("detail.fields.timestamp")}
                value={fmtDate(geofence.timestamp)}
              />
              <FieldRow
                label={t("detail.fields.sdkLatitude")}
                value={geofence.latitude}
              />
              <FieldRow
                label={t("detail.fields.sdkLongitude")}
                value={geofence.longitude}
              />
              <FieldRow
                label={t("detail.fields.accuracy")}
                value={
                  geofence.accuracy ? t(`accuracy.${geofence.accuracy}`) : "-"
                }
              />
              <FieldRow
                label={t("detail.fields.geofenceIdentifier")}
                value={geofence.geofenceIdentifier || "-"}
              />
              <FieldRow
                label={t("detail.fields.source")}
                value={
                  geofence.source
                    ? tt(`source.${geofence.source}`)
                    : "-"
                }
              />
              <FieldRow
                label={t("detail.fields.deviceId")}
                value={geofence.deviceId || "-"}
              />
              <FieldRow
                label={t("detail.fields.speed")}
                value={
                  geofence.speed != null ? `${geofence.speed} m/s` : "-"
                }
              />
              <FieldRow
                label={t("detail.fields.batteryLevel")}
                value={
                  geofence.batteryLevel != null
                    ? `${geofence.batteryLevel}%`
                    : "-"
                }
              />
              <FieldRow
                label={t("detail.fields.batteryCharging")}
                value={
                  geofence.batteryCharging != null
                    ? geofence.batteryCharging
                      ? "Yes"
                      : "No"
                    : "-"
                }
              />
              <FieldRow
                label={t("detail.fields.osType")}
                value={geofence.osType || "-"}
              />
              <FieldRow
                label={t("detail.fields.uuid")}
                value={geofence.uuid || "-"}
              />
              {geofence.error && (
                <FieldRow
                  label={t("detail.fields.error")}
                  value={
                    <Typography variant="body2" color="error">
                      {geofence.error}
                    </Typography>
                  }
                />
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Participant + Address */}
        <Grid item xs={12} md={6}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 700 }}>
                {t("detail.participantInfo")}
              </Typography>
              {geofence.participant ? (
                <>
                  <FieldRow
                    label={t("detail.fields.participantName")}
                    value={
                      <Link
                        sx={{ cursor: "pointer" }}
                        onClick={() =>
                          navigate(
                            `/participants/${geofence.participant?._id}`,
                          )
                        }
                      >
                        {geofence.participant.firstName}{" "}
                        {geofence.participant.lastName}
                      </Link>
                    }
                  />
                  <FieldRow
                    label={t("detail.fields.email")}
                    value={geofence.participant.email || "-"}
                  />
                  <FieldRow
                    label={t("detail.fields.phoneNumber")}
                    value={
                      geofence.participant.phoneNumber
                        ? `${geofence.participant.countryPhoneCode || ""} ${
                            geofence.participant.phoneNumber
                          }`.trim()
                        : "-"
                    }
                  />
                  <FieldRow
                    label={t("detail.fields.participantStatus")}
                    value={geofence.participant.status || "-"}
                  />
                </>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  -
                </Typography>
              )}

              {address ? (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 600,
                      mb: 1,
                      color: !isSecondary ? "primary.main" : undefined,
                    }}
                  >
                    {t("detail.addressPrimary")}
                    {!isSecondary && " ●"}
                  </Typography>
                  <FieldRow
                    label={t("detail.fields.addressLine1")}
                    value={primaryAddressLine || "-"}
                  />
                  <FieldRow
                    label={t("detail.fields.city")}
                    value={address.city || "-"}
                  />
                  <FieldRow
                    label={t("detail.fields.state")}
                    value={address.state || "-"}
                  />
                  <FieldRow
                    label={t("detail.fields.postalCode")}
                    value={address.postalCode || address.zipCode || "-"}
                  />
                  <FieldRow
                    label={t("detail.fields.addressLatitude")}
                    value={address.latitude ?? "-"}
                  />
                  <FieldRow
                    label={t("detail.fields.addressLongitude")}
                    value={address.longitude ?? "-"}
                  />

                  {(address.secondaryAddressLine1 ||
                    address.secondaryLatitude != null) && (
                    <>
                      <Divider sx={{ my: 2 }} />
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontWeight: 600,
                          mb: 1,
                          color: isSecondary ? "primary.main" : undefined,
                        }}
                      >
                        {t("detail.addressSecondary")}
                        {isSecondary && " ●"}
                      </Typography>
                      <FieldRow
                        label={t("detail.fields.addressLine1")}
                        value={secondaryAddressLine || "-"}
                      />
                      <FieldRow
                        label={t("detail.fields.city")}
                        value={address.secondaryCity || "-"}
                      />
                      <FieldRow
                        label={t("detail.fields.state")}
                        value={address.secondaryState || "-"}
                      />
                      <FieldRow
                        label={t("detail.fields.postalCode")}
                        value={
                          address.secondaryPostalCode ||
                          address.secondaryZipCode ||
                          "-"
                        }
                      />
                      <FieldRow
                        label={t("detail.fields.addressLatitude")}
                        value={address.secondaryLatitude ?? "-"}
                      />
                      <FieldRow
                        label={t("detail.fields.addressLongitude")}
                        value={address.secondaryLongitude ?? "-"}
                      />
                    </>
                  )}
                </>
              ) : (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="body2" color="text.secondary">
                    {t("detail.noAddress")}
                  </Typography>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Program & Enrollment & Geofence Configuration */}
        <Grid item xs={12}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 700 }}>
                {t("detail.programConfig")}
              </Typography>
              <FieldRow
                label={t("detail.fields.programName")}
                value={geofence.program?.name || "-"}
              />
              <FieldRow
                label={t("detail.fields.programId")}
                value={geofence.program?._id || "-"}
              />

              {geofence.enrollment && (
                <>
                  <FieldRow
                    label={t("detail.fields.enrollmentId")}
                    value={geofence.enrollment._id}
                  />
                  <FieldRow
                    label={t("detail.fields.enrollmentStatus")}
                    value={
                      <Chip
                        label={geofence.enrollment.status}
                        size="small"
                        variant="outlined"
                      />
                    }
                  />
                  <FieldRow
                    label={t("detail.fields.enrollmentStartDate")}
                    value={fmtDate(
                      geofence.enrollment.startDate ||
                        geofence.enrollment.startedAt,
                    )}
                  />
                  <FieldRow
                    label={t("detail.fields.enrollmentEndDate")}
                    value={fmtDate(
                      geofence.enrollment.endDate ||
                        geofence.enrollment.completedAt,
                    )}
                  />
                </>
              )}

              {validationSettings && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Grid container spacing={0}>
                    {geofenceSettingsFields.map((field) => {
                      const value = validationSettings[field];
                      if (value === undefined || value === null) return null;

                      let displayValue: React.ReactNode;
                      if (typeof value === "boolean") {
                        displayValue = (
                          <Chip
                            label={value ? "Yes" : "No"}
                            size="small"
                            color={value ? "success" : "default"}
                            variant="outlined"
                          />
                        );
                      } else {
                        displayValue = String(value);
                      }

                      return (
                        <Grid item xs={12} md={6} key={field}>
                          <FieldRow
                            label={tt(`detail.fields.${field}`)}
                            value={displayValue}
                          />
                        </Grid>
                      );
                    })}
                  </Grid>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Test Result lifecycle */}
        <Grid item xs={12}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 700 }}>
                {t("detail.testResult")}
              </Typography>
              {geofence.testResult ? (
                <Grid container spacing={0}>
                  <Grid item xs={12} md={6}>
                    <FieldRow
                      label={t("detail.fields.testResultId")}
                      value={geofence.testResult._id}
                    />
                    <FieldRow
                      label={t("detail.fields.trStatus")}
                      value={
                        <Chip
                          label={geofence.testResult.status}
                          size="small"
                          variant="outlined"
                        />
                      }
                    />
                    <FieldRow
                      label={t("detail.fields.trValidationType")}
                      value={geofence.testResult.validationType}
                    />
                    <FieldRow
                      label={t("detail.fields.trValidationNumber")}
                      value={geofence.testResult.validationNumber ?? "-"}
                    />
                    <FieldRow
                      label={t("detail.fields.trIsOnDemand")}
                      value={
                        geofence.testResult.isOnDemand != null
                          ? geofence.testResult.isOnDemand
                            ? "Yes"
                            : "No"
                          : "-"
                      }
                    />
                    <FieldRow
                      label={t("detail.fields.trCreatedAt")}
                      value={fmtDate(geofence.testResult.createdAt)}
                    />
                    <FieldRow
                      label={t("detail.fields.trUpdatedAt")}
                      value={fmtDate(geofence.testResult.updatedAt)}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FieldRow
                      label={t("detail.fields.trNotificationSentAt")}
                      value={fmtDate(geofence.testResult.notificationSentAt)}
                    />
                    <FieldRow
                      label={t("detail.fields.trValidationStartedAt")}
                      value={fmtDate(geofence.testResult.validationStartedAt)}
                    />
                    <FieldRow
                      label={t("detail.fields.trValidationActionedAt")}
                      value={fmtDate(geofence.testResult.validationActionedAt)}
                    />
                    <FieldRow
                      label={t("detail.fields.trValidationExpiredAt")}
                      value={fmtDate(geofence.testResult.validationExpiredAt)}
                    />
                    <FieldRow
                      label={t("detail.fields.trCompletedAt")}
                      value={fmtDate(geofence.testResult.completedAt)}
                    />
                    <FieldRow
                      label={t("detail.fields.trCarriageResponse")}
                      value={
                        geofence.testResult.carriageConfirmationResponse || "-"
                      }
                    />
                    <FieldRow
                      label={t("detail.fields.trCarriageOpenedAt")}
                      value={fmtDate(
                        geofence.testResult.carriageConfirmationOpenedAt,
                      )}
                    />
                    <FieldRow
                      label={t("detail.fields.trCarriageRespondedAt")}
                      value={fmtDate(
                        geofence.testResult.carriageConfirmationRespondedAt,
                      )}
                    />
                  </Grid>
                  {geofence.testResult.missedValidationMetadata && (
                    <Grid item xs={12}>
                      <FieldRow
                        label={t("detail.fields.trMissedMetadata")}
                        value={renderMetadata(
                          geofence.testResult.missedValidationMetadata,
                        )}
                      />
                    </Grid>
                  )}
                  {geofence.testResult.nonCarriageMetadata && (
                    <Grid item xs={12}>
                      <FieldRow
                        label={t("detail.fields.trNonCarriageMetadata")}
                        value={renderMetadata(
                          geofence.testResult.nonCarriageMetadata,
                        )}
                      />
                    </Grid>
                  )}
                </Grid>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  {t("detail.noTestResult")}
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Notification */}
        <Grid item xs={12}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 700 }}>
                {t("detail.notification")}
              </Typography>
              {geofence.notification ? (
                <Grid container spacing={0}>
                  <Grid item xs={12} md={6}>
                    <FieldRow
                      label={t("detail.fields.notificationId")}
                      value={geofence.notification._id}
                    />
                    <FieldRow
                      label={t("detail.fields.notifChannel")}
                      value={geofence.notification.channel}
                    />
                    <FieldRow
                      label={t("detail.fields.notifStatus")}
                      value={geofence.notification.status || "-"}
                    />
                    <FieldRow
                      label={t("detail.fields.notifType")}
                      value={geofence.notification.type}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FieldRow
                      label={t("detail.fields.notifSentAt")}
                      value={fmtDate(geofence.notification.sentAt)}
                    />
                    <FieldRow
                      label={t("detail.fields.notifCreatedAt")}
                      value={fmtDate(geofence.notification.createdAt)}
                    />
                  </Grid>
                </Grid>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  {t("detail.noNotification")}
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default GeofenceDetailPage;
